import type { Contact } from "./types";

export type ContactListSortDirection = "asc" | "desc";

export type ContactListVisibleSortField =
  | "name"
  | "email"
  | "company"
  | "status"
  | "lead_type"
  | "score"
  | "source"
  | "created_at";

export type ContactListServerSortColumn =
  | "first_name"
  | "last_name"
  | "email"
  | "company"
  | "status"
  | "lead_type"
  | "source"
  | "created_at";

export interface ContactListSort {
  field: ContactListVisibleSortField;
  direction: ContactListSortDirection;
}

export interface ContactListOrderClause {
  column: ContactListServerSortColumn;
  ascending: boolean;
  nullsFirst: boolean;
}

export const DEFAULT_CONTACT_LIST_SORT: ContactListSort = {
  field: "created_at",
  direction: "desc",
};

const DESC_FIRST_FIELDS = new Set<ContactListVisibleSortField>([
  "score",
  "created_at",
]);

const SERVER_ORDER_COLUMNS: Record<
  Exclude<ContactListVisibleSortField, "score">,
  ContactListServerSortColumn[]
> = {
  name: ["first_name", "last_name", "email"],
  email: ["email", "first_name", "last_name"],
  company: ["company", "first_name", "last_name"],
  status: ["status", "first_name", "last_name"],
  lead_type: ["lead_type", "first_name", "last_name"],
  source: ["source", "first_name", "last_name"],
  created_at: ["created_at", "first_name", "last_name"],
};

export function defaultContactListSortDirection(
  field: ContactListVisibleSortField,
): ContactListSortDirection {
  return DESC_FIRST_FIELDS.has(field) ? "desc" : "asc";
}

export function nextContactListSort(
  current: ContactListSort,
  field: ContactListVisibleSortField,
): ContactListSort {
  if (current.field !== field) {
    return { field, direction: defaultContactListSortDirection(field) };
  }

  return {
    field,
    direction: current.direction === "asc" ? "desc" : "asc",
  };
}

export function isDefaultContactListSort(sort: ContactListSort): boolean {
  return (
    sort.field === DEFAULT_CONTACT_LIST_SORT.field &&
    sort.direction === DEFAULT_CONTACT_LIST_SORT.direction
  );
}

export function shouldFetchAllContactsForLocalSort(
  hasLocalListFilters: boolean,
  sort: ContactListSort,
): boolean {
  if (sort.field === "score") return true;
  return hasLocalListFilters && !isDefaultContactListSort(sort);
}

export function getContactListOrderClauses(
  sort: ContactListSort = DEFAULT_CONTACT_LIST_SORT,
): ContactListOrderClause[] {
  const field: Exclude<ContactListVisibleSortField, "score"> =
    sort.field === "score" ? "created_at" : sort.field;
  const primaryAscending =
    sort.field === "score"
      ? DEFAULT_CONTACT_LIST_SORT.direction === "asc"
      : sort.direction === "asc";

  return SERVER_ORDER_COLUMNS[field].map((column, index) => ({
    column,
    ascending: field === "name" || index === 0 ? primaryAscending : true,
    nullsFirst: false,
  }));
}

type ScoreSortableContact = Pick<
  Contact,
  "id" | "first_name" | "last_name" | "email" | "created_at"
>;

function normalizedText(value: string | null | undefined): string | null {
  const text = value?.trim().toLowerCase() ?? "";
  return text || null;
}

function contactName(contact: ScoreSortableContact): string | null {
  const name = [contact.first_name, contact.last_name]
    .filter(Boolean)
    .join(" ")
    .trim();
  return normalizedText(name || contact.email);
}

function dateTime(value: string | null | undefined): number | null {
  if (!value) return null;
  const time = new Date(value).getTime();
  return Number.isNaN(time) ? null : time;
}

function compareNullableNumber(
  a: number | null | undefined,
  b: number | null | undefined,
  direction: ContactListSortDirection,
): number {
  if (a == null && b == null) return 0;
  if (a == null) return 1;
  if (b == null) return -1;
  const comparison = a - b;
  return direction === "asc" ? comparison : -comparison;
}

function compareNullableText(
  a: string | null | undefined,
  b: string | null | undefined,
): number {
  const aText = normalizedText(a);
  const bText = normalizedText(b);
  if (!aText && !bText) return 0;
  if (!aText) return 1;
  if (!bText) return -1;
  return aText.localeCompare(bText);
}

function compareNullableDateDesc(
  a: string | null | undefined,
  b: string | null | undefined,
): number {
  const aTime = dateTime(a);
  const bTime = dateTime(b);
  if (aTime === null && bTime === null) return 0;
  if (aTime === null) return 1;
  if (bTime === null) return -1;
  return bTime - aTime;
}

export function sortContactsByScore<T extends ScoreSortableContact>(
  contacts: readonly T[],
  scoresByContactId: ReadonlyMap<string, number>,
  direction: ContactListSortDirection,
): T[] {
  return contacts
    .map((contact, index) => ({ contact, index }))
    .sort((a, b) => {
      const scoreComparison = compareNullableNumber(
        scoresByContactId.get(a.contact.id),
        scoresByContactId.get(b.contact.id),
        direction,
      );
      if (scoreComparison !== 0) return scoreComparison;

      const nameComparison = compareNullableText(
        contactName(a.contact),
        contactName(b.contact),
      );
      if (nameComparison !== 0) return nameComparison;

      const createdComparison = compareNullableDateDesc(
        a.contact.created_at,
        b.contact.created_at,
      );
      if (createdComparison !== 0) return createdComparison;

      return a.index - b.index;
    })
    .map(({ contact }) => contact);
}
